import React, { useCallback, useEffect, useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import {
  SingleDropdown,
  SimpleSingleDropdown,
  MultiDropdown,
  useSingleDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  standardRequestWithErrorFlash,
  standardRequestWithFlash,
  standardRequest,
} from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import {
  toMultiDropdownOptions,
  toNestedDropdownOptions,
  type Option,
} from 'shared/utils/formUtils';
import {
  SecondaryBody,
  SectionHeader,
} from '@grnhse/seedling/lib/birch/typography';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { createBulkRediscoveryActions } from 'talent_matching/api';
import type { RediscoveryApplication } from 'talent_matching/types';

const t = withLocalePrefix(
  'talent_matching.rediscovery.bulk_actions.add_to_prospect_process'
);

const tProspect = withLocalePrefix(
  'talent_matching.rediscovery.table.prospect_modal'
);

type ProspectPool = {
  id: number;
  name: string;
  prospect_pool_stages: Array<{ id: number; name: string }>;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  applicationIds: number[];
  totalSelected: number;
};

const CACHE_STALE_TIME = 1000 * 60 * 5;

const extractItems = (data: Record<string, unknown>): Option[] => {
  if (data.results) {
    return data.results as Option[];
  }
  if (data.items) {
    return data.items as Option[];
  }
  if (data.data) {
    return data.data as Option[];
  }
  return data as unknown as Option[];
};

const toOptions = (results: Option[], nested = false) => {
  if (nested) {
    const nonEmpty = results.filter(
      (opt) => !opt.children || opt.children.length > 0
    );
    return toNestedDropdownOptions(nonEmpty);
  }
  return toMultiDropdownOptions(results);
};

const useFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>,
  onRawData?: (rawItems: Option[]) => void,
  nested = false
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (term: string): Promise<{ items: Item[] }> => {
      const queryKey = ['prospect-modal', path, term, params];

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return { items: toOptions(rawItems, nested), rawItems };
        },
        staleTime: CACHE_STALE_TIME,
      });

      onRawData?.(result.rawItems);
      return { items: result.items };
    },
    [path, params, onRawData, queryClient, nested]
  );
};

const usePaginatedFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      term: string,
      nextPage: number
    ): Promise<{ items: Item[]; more: boolean }> => {
      const nested = Boolean(params?.nested);
      const queryKey = ['prospect-modal', path, term, nextPage, params];

      const cached = queryClient.getQueryData<{ items: Item[]; more: boolean }>(
        queryKey
      );
      if (cached) {
        return cached;
      }

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: nextPage || 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return {
            items: toOptions(rawItems, nested),
            more: response.data.more ?? false,
          };
        },
        staleTime: CACHE_STALE_TIME,
      });

      return result;
    },
    [path, params, queryClient]
  );
};

const BulkAddToProspectProcessModal = ({
  isOpen,
  closeModal,
  applicationIds,
  totalSelected,
}: Props) => {
  const { canCreateSources, currentUserName, userId, hiringPlanId } =
    useUserContext();
  const {
    paths: {
      jobsPath,
      departmentsPath,
      officesPath,
      prospectPoolsPath,
      prospectOwnerOptionsPath,
      sourceOptionsPath,
      referrerOptionsPath,
      sourceTypeOptionsPath,
    },
    defaultPoolName,
  } = useRediscoveryTabContext();

  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const defaultOwner: Item = {
    label: currentUserName,
    value: userId.toString(),
  };

  const [selectedJobs, setSelectedJobs] = useState<Item[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Item | null>(
    null
  );
  const [selectedOffice, setSelectedOffice] = useState<Item | null>(null);
  const [selectedPool, setSelectedPool] = useState<Item | null>(null);
  const [selectedStage, setSelectedStage] = useState<Item | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<Item | null>(defaultOwner);
  const [selectedSource, setSelectedSource] = useState<Item | null>(null);
  const [selectedReferrer, setSelectedReferrer] = useState<Item | null>(null);

  const [customSourceOpen, setCustomSourceOpen] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState<Item | null>(
    null
  );
  const [customSourceText, setCustomSourceText] = useState('');
  const [customReferrerOpen, setCustomReferrerOpen] = useState(false);
  const [customReferrerText, setCustomReferrerText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedJobs([]);
      setSelectedDepartment(null);
      setSelectedOffice(null);
      setSelectedPool(null);
      setSelectedStage(null);
      setSelectedOwner(defaultOwner);
      setSelectedSource(null);
      setSelectedReferrer(null);
      setCustomSourceOpen(false);
      setSelectedSourceType(null);
      setCustomSourceText('');
      setCustomReferrerOpen(false);
      setCustomReferrerText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setSelectedOffice(null);
  }, [selectedDepartment]);

  useEffect(() => {
    setSelectedStage(null);
  }, [selectedPool]);

  const [officeKey, setOfficeKey] = useState(0);
  useEffect(() => {
    setOfficeKey((k) => k + 1);
  }, [selectedDepartment]);

  const fetchJobs = usePaginatedFetchItems(jobsPath as string, {
    nested: true,
  });
  const fetchDepartments = useFetchItems(departmentsPath as string);
  const fetchOffices = usePaginatedFetchItems(officesPath as string, {
    ...(selectedDepartment ? { department_id: selectedDepartment.value } : {}),
  });

  const [stagesByPoolId, setStagesByPoolId] = useState<Record<string, Item[]>>(
    {}
  );

  const storePoolStages = useCallback((rawItems: Option[]) => {
    const map: Record<string, Item[]> = {};
    rawItems.forEach((group) => {
      if (!group.children) {
        return;
      }
      const pools = group.children as unknown as ProspectPool[];
      pools.forEach((pool) => {
        map[pool.id.toString()] = (pool.prospect_pool_stages || []).map(
          (stage) => ({
            value: stage.id.toString(),
            label: stage.name,
          })
        );
      });
    });
    setStagesByPoolId(map);
  }, []);

  const fetchPools = useFetchItems(
    prospectPoolsPath as string,
    { grouped: 1 },
    storePoolStages,
    true
  );

  const stageOptions = selectedPool
    ? stagesByPoolId[selectedPool.value] || []
    : [];

  const fetchOwners = usePaginatedFetchItems(
    prospectOwnerOptionsPath as string
  );
  const fetchSources = useFetchItems(sourceOptionsPath as string);
  const fetchReferrers = usePaginatedFetchItems(referrerOptionsPath as string, {
    remove_inactive: true,
  });

  const fetchSourceTypes = useCallback(
    async (term: string): Promise<{ items: Item[] }> => {
      if (!sourceTypeOptionsPath) {
        return { items: [] };
      }
      const result = await standardRequest.get(sourceTypeOptionsPath, {
        params: { term, react_select: true },
      });
      return { items: result.data.results };
    },
    [sourceTypeOptionsPath]
  );

  const poolHelperText =
    !selectedPool && defaultPoolName
      ? tProspect('default_pool_helper', { pool_name: defaultPoolName })
      : '';

  const jobsDropdown = useMultiDropdown({
    fetchItems: fetchJobs,
    paginated: true,
    selectedItems: selectedJobs,
    onSelectedItemsChange: (changes) =>
      setSelectedJobs(changes.selectedItems ?? []),
    preventLoadOnMount: true,
  });

  const departmentsDropdown = useSingleDropdown({
    fetchItems: fetchDepartments,
    selectedItem: selectedDepartment,
    onSelectedItemChange: (e) => setSelectedDepartment(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const officesDropdown = useSingleDropdown({
    fetchItems: fetchOffices,
    paginated: true,
    selectedItem: selectedOffice,
    onSelectedItemChange: (e) => setSelectedOffice(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const poolsDropdown = useSingleDropdown({
    fetchItems: fetchPools,
    selectedItem: selectedPool,
    onSelectedItemChange: (e) => setSelectedPool(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const ownersDropdown = useSingleDropdown({
    fetchItems: fetchOwners,
    paginated: true,
    selectedItem: selectedOwner,
    onSelectedItemChange: (e) => setSelectedOwner(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const sourcesDropdown = useSingleDropdown({
    fetchItems: fetchSources,
    selectedItem: selectedSource,
    onSelectedItemChange: (e) => setSelectedSource(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const sourceTypesDropdown = useSingleDropdown({
    fetchItems: fetchSourceTypes,
    selectedItem: selectedSourceType,
    onSelectedItemChange: (e) => setSelectedSourceType(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const referrersDropdown = useSingleDropdown({
    fetchItems: fetchReferrers,
    paginated: true,
    selectedItem: selectedReferrer,
    onSelectedItemChange: (e) => setSelectedReferrer(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const toggleCustomSource = () => {
    setCustomSourceOpen((prev) => {
      if (prev) {
        setSelectedSourceType(null);
        setCustomSourceText('');
      } else {
        setSelectedSource(null);
      }
      return !prev;
    });
  };

  const toggleCustomReferrer = () => {
    setCustomReferrerOpen((prev) => {
      if (prev) {
        setCustomReferrerText('');
      }
      return !prev;
    });
  };

  const isCustomSourceValid =
    !customSourceOpen ||
    (!!selectedSourceType && customSourceText.trim().length > 0);
  const isCustomReferrerValid =
    !customReferrerOpen || customReferrerText.trim().length > 0;
  const canSubmit = !submitting && isCustomSourceValid && isCustomReferrerValid;

  const onSubmit = async () => {
    setSubmitting(true);

    const formData = new FormData();

    selectedJobs.forEach((job) =>
      formData.append('prospective_hiring_plan_ids[]', job.value)
    );

    applicationIds.forEach((id) =>
      formData.append('application_ids[]', id.toString())
    );

    if (selectedDepartment) {
      formData.append('prospective_department_id', selectedDepartment.value);
    }
    if (selectedOffice) {
      formData.append('prospective_office_id', selectedOffice.value);
    }
    if (selectedPool) {
      formData.append('prospect_pool_id', selectedPool.value);
    }
    if (selectedStage) {
      formData.append('prospect_pool_stage_id', selectedStage.value);
    }
    if (selectedOwner) {
      formData.append('prospect_owner_id', selectedOwner.value);
    }

    if (customSourceOpen) {
      formData.append(
        'custom_source_strategy_id',
        (selectedSourceType as Item).value
      );
      formData.append('custom_source', customSourceText.trim());
    } else if (selectedSource) {
      formData.append('source_id', selectedSource.value);
    }

    if (customReferrerOpen) {
      formData.append('referred_by', customReferrerText.trim());
    } else if (selectedReferrer) {
      formData.append('referred_by_id', selectedReferrer.value);
    }

    try {
      await standardRequestWithFlash.post(
        '/people/bulk/add_prospects',
        formData,
        {
          successMessage: t('success', { count: totalSelected }),
        }
      );
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            key[0] === 'prospect-modal' &&
            (key[1] === sourceOptionsPath || key[1] === referrerOptionsPath)
          );
        },
      });
      createBulkRediscoveryActions({
        action_type: 'added_as_prospect',
        application_ids: applicationIds,
        hiring_plan_id: hiringPlanId,
        metadata: {
          prospect_pool_id: selectedPool?.value,
          prospect_pool_stage_id: selectedStage?.value,
          prospect_owner_id: selectedOwner?.value,
        },
      });
      const now = new Date().toISOString();
      applicationIds.forEach((id, index) => {
        const cached = queryClient.getQueryData<RediscoveryApplication>([
          'rediscovery-application',
          id,
        ]);
        if (!cached) {
          return;
        }
        queryClient.setQueryData(['rediscovery-application', id], {
          ...cached,
          rediscoveryActions: [
            {
              id: -(index + 1),
              action: 'added_as_prospect',
              metadata: null,
              organization_id: 0,
              hiring_plan_id: hiringPlanId,
              application_id: id,
              user_id: userId,
              user_name: currentUserName,
              created_at: now,
              updated_at: now,
            },
            ...cached.rediscoveryActions,
          ],
        });
      });
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title', { count: totalSelected })}
      size="medium"
    >
      <DataEntryModal.Header onClick={closeModal}>
        {t('title', { count: totalSelected })}
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <FullWidth>
          <Stack spacing={3} sx={{ paddingBottom: 3 }}>
            <Stack spacing={3}>
              <SectionHeader>
                {tProspect('job_department_office')}
              </SectionHeader>

              <MultiDropdown dropdownProps={jobsDropdown}>
                <MultiDropdown.Field
                  label={tProspect('job_name')}
                  placeholder={tProspect('job_name_placeholder')}
                />
                <MultiDropdown.Flyout />
              </MultiDropdown>

              <SecondaryBody>{tProspect('andor')}</SecondaryBody>

              <SingleDropdown dropdownProps={departmentsDropdown}>
                <SingleDropdown.Field
                  label={tProspect('department')}
                  placeholder={tProspect('department_placeholder')}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>

              <SingleDropdown
                key={`office-${officeKey}`}
                dropdownProps={officesDropdown}
              >
                <SingleDropdown.Field
                  label={tProspect('office')}
                  disabled={!selectedDepartment}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>
            </Stack>

            <Divider />

            <Stack spacing={3}>
              <SectionHeader>{tProspect('pool_section')}</SectionHeader>

              <SingleDropdown
                key={`pool-${selectedPool?.value}`}
                dropdownProps={poolsDropdown}
              >
                <SingleDropdown.Field
                  label={tProspect('pool')}
                  placeholder={tProspect('pool_placeholder')}
                  helperText={poolHelperText}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>

              <SimpleSingleDropdown
                key={`stage-${selectedPool?.value ?? 'none'}`}
                label={tProspect('stage')}
                placeholder={tProspect('stage_placeholder')}
                items={stageOptions}
                selectedItem={selectedStage}
                onSelectedItemChange={setSelectedStage}
                disabled={!selectedPool || stageOptions.length === 0}
              />
            </Stack>

            <Divider />

            <Stack spacing={3}>
              <SectionHeader>
                {tProspect('owner_referrer_source')}
              </SectionHeader>

              <div className="pii-blur">
                <SingleDropdown dropdownProps={ownersDropdown}>
                  <SingleDropdown.Field
                    label={tProspect('owner')}
                    placeholder={tProspect('owner_placeholder')}
                  />
                  <SingleDropdown.Flyout />
                </SingleDropdown>
              </div>

              <Stack spacing={1}>
                <div className="pii-blur">
                  {customSourceOpen ? (
                    <Stack spacing={3}>
                      <SingleDropdown dropdownProps={sourceTypesDropdown}>
                        <SingleDropdown.Field
                          label={tProspect('source_type')}
                          placeholder={tProspect('source_type_placeholder')}
                          required
                        />
                        <SingleDropdown.Flyout />
                      </SingleDropdown>
                      <TextInput
                        label={tProspect('custom_source')}
                        value={customSourceText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCustomSourceText(e.target.value)
                        }
                        required
                      />
                    </Stack>
                  ) : (
                    <SingleDropdown dropdownProps={sourcesDropdown}>
                      <SingleDropdown.Field
                        label={tProspect('source')}
                        placeholder={tProspect('source_placeholder')}
                      />
                      <SingleDropdown.Flyout />
                    </SingleDropdown>
                  )}
                </div>
                {canCreateSources && (
                  <Stack alignItems="flex-start">
                    <TertiaryButton size="md" onClick={toggleCustomSource}>
                      {customSourceOpen
                        ? tProspect('existing_source')
                        : tProspect('add_source')}
                    </TertiaryButton>
                  </Stack>
                )}
              </Stack>

              <Stack spacing={1}>
                <div className="pii-blur">
                  {customReferrerOpen ? (
                    <TextInput
                      label={tProspect('referrer')}
                      value={customReferrerText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomReferrerText(e.target.value)
                      }
                      required
                    />
                  ) : (
                    <SingleDropdown dropdownProps={referrersDropdown}>
                      <SingleDropdown.Field
                        label={tProspect('referrer')}
                        placeholder={tProspect('referrer_placeholder')}
                      />
                      <SingleDropdown.Flyout />
                    </SingleDropdown>
                  )}
                </div>
                {canCreateSources && (
                  <Stack alignItems="flex-start">
                    <TertiaryButton size="md" onClick={toggleCustomReferrer}>
                      {customReferrerOpen
                        ? tProspect('existing_referrer')
                        : tProspect('add_referrer')}
                    </TertiaryButton>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </FullWidth>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SecondaryButton onClick={closeModal} disabled={submitting}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton onClick={onSubmit} disabled={!canSubmit}>
            {t('submit')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default BulkAddToProspectProcessModal;
